import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, ShieldCheck, Lock, LayoutTemplate, Save, Trash2 } from "lucide-react";
import Navbar from "../components/Navbar";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function CreateForm() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [savingTpl, setSavingTpl] = useState(false);
  const [f, setF] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    merchant_name: user?.business_name || "",
    merchant_descriptor: "",
    amount: "",
    currency: "USD",
    frequency: "one-time",
    frequency_note: "",
    description: "",
    authorization_terms: "",
  });

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const loadTemplates = async () => {
    try {
      const res = await api.get("/templates");
      setTemplates(res.data || []);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { loadTemplates(); }, []);

  const applyTemplate = (e) => {
    const id = e.target.value;
    if (!id) return;
    const t = templates.find((x) => x.template_id === id);
    if (!t) return;
    setF((prev) => ({
      ...prev,
      merchant_name: t.merchant_name || "",
      merchant_descriptor: t.merchant_descriptor || "",
      amount: t.amount != null ? String(t.amount) : "",
      currency: t.currency || "USD",
      frequency: t.frequency || "one-time",
      frequency_note: t.frequency_note || "",
      description: t.description || "",
      authorization_terms: t.authorization_terms || "",
    }));
    toast.success(`Applied template "${t.name}"`);
  };

  const saveTemplate = async () => {
    if (!f.merchant_name) {
      toast.error("Add a merchant name before saving a template");
      return;
    }
    const name = window.prompt("Name this template (e.g. Monthly retainer)", f.description || f.merchant_name);
    if (name === null) return;
    setSavingTpl(true);
    try {
      await api.post("/templates", {
        name: name || f.merchant_name,
        merchant_name: f.merchant_name,
        merchant_descriptor: f.merchant_descriptor,
        amount: f.amount ? parseFloat(f.amount) : null,
        currency: f.currency,
        frequency: f.frequency,
        frequency_note: f.frequency_note,
        description: f.description,
        authorization_terms: f.authorization_terms,
      });
      toast.success("Template saved");
      loadTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save template");
    } finally {
      setSavingTpl(false);
    }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await api.delete(`/templates/${id}`);
      toast.success("Template deleted");
      loadTemplates();
    } catch (e) {
      toast.error("Failed to delete template");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.customer_name || !f.merchant_name || !f.amount) {
      toast.error("Customer, merchant and amount are required");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/forms", { ...f, amount: parseFloat(f.amount) });
      toast.success("Authorization form created");
      navigate(`/forms/${res.data.form_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create form");
    } finally {
      setBusy(false);
    }
  };

  const money = (n) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n) || 0);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <main className="max-w-6xl mx-auto px-5 py-8">
        <button onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#64748B] hover:text-[#0F172A] mb-5">
          <ArrowLeft size={16} /> Back to dashboard
        </button>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Form */}
          <form onSubmit={submit} className="lg:col-span-3 space-y-6">
            <h1 className="text-2xl font-extrabold tracking-tight text-[#0F172A]">New authorization</h1>

            {templates.length > 0 && (
              <div className="rounded-xl border border-[#E2E8F0] bg-white p-5" data-testid="template-picker-card">
                <div className="flex items-center gap-2 mb-3">
                  <LayoutTemplate size={16} className="text-[#2563EB]" />
                  <h3 className="text-sm font-bold text-[#0F172A]">Start from a template</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {templates.map((t) => (
                    <div key={t.template_id} className="group inline-flex items-center gap-1.5 rounded-full border border-[#E2E8F0] bg-slate-50 pl-3 pr-1.5 py-1">
                      <button type="button" data-testid={`template-apply-${t.template_id}`} onClick={() => applyTemplate({ target: { value: t.template_id } })} className="text-xs font-semibold text-[#0F172A] hover:text-[#2563EB] transition-colors">
                        {t.name}
                      </button>
                      <button type="button" data-testid={`template-delete-${t.template_id}`} onClick={() => deleteTemplate(t.template_id)} className="text-[#CBD5E1] hover:text-[#DC2626] transition-colors">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Section title="Customer">
              <Field label="Customer name" testid="form-customer-name-input" value={f.customer_name} onChange={set("customer_name")} required />
              <Field label="Customer email" testid="form-customer-email-input" type="email" value={f.customer_email} onChange={set("customer_email")} placeholder="For emailing the signing link" />
              <Field label="Customer mobile" testid="form-customer-phone-input" type="tel" value={f.customer_phone} onChange={set("customer_phone")} placeholder="+14155552671 — for texting the link" />
            </Section>

            <Section title="Merchant">
              <Field label="Merchant name" testid="form-merchant-name-input" value={f.merchant_name} onChange={set("merchant_name")} required />
              <Field label="Statement descriptor" testid="form-merchant-descriptor-input" value={f.merchant_descriptor} onChange={set("merchant_descriptor")} placeholder="How it appears on card statement" />
            </Section>

            <Section title="Authorization terms">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Amount" testid="form-auth-amount-input" type="number" step="0.01" min="0" value={f.amount} onChange={set("amount")} required />
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Frequency</span>
                  <select data-testid="form-frequency-select" value={f.frequency} onChange={set("frequency")} className="mt-1.5 w-full rounded-lg border border-[#E2E8F0] px-3 py-2.5 text-sm bg-white outline-none focus:border-[#2563EB]">
                    <option value="one-time">One-time</option>
                    <option value="recurring">Recurring</option>
                  </select>
                </label>
              </div>
              {f.frequency === "recurring" && (
                <Field label="Billing schedule" testid="form-frequency-note-input" value={f.frequency_note} onChange={set("frequency_note")} placeholder="e.g. Monthly on the 1st" />
              )}
              <Field label="Description" testid="form-description-input" value={f.description} onChange={set("description")} placeholder="What is being charged for" />
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Custom authorization statement</span>
                <textarea data-testid="form-terms-input" value={f.authorization_terms} onChange={set("authorization_terms")} rows={3} placeholder="Leave blank to use the standard authorization language" className="mt-1.5 w-full rounded-lg border border-[#E2E8F0] px-3 py-2.5 text-sm outline-none focus:border-[#2563EB] resize-none" />
              </label>
            </Section>

            <div className="flex flex-wrap items-center gap-3">
              <button type="submit" data-testid="form-submit-create-btn" disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-[#2563EB] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#1D4ED8] transition-all active:scale-[0.99] disabled:opacity-60">
                {busy && <Loader2 size={16} className="animate-spin" />} Create &amp; get signing link
              </button>
              <button type="button" onClick={saveTemplate} data-testid="save-template-btn" disabled={savingTpl} className="inline-flex items-center gap-2 rounded-lg border border-[#E2E8F0] px-4 py-2.5 text-sm font-semibold text-[#0F172A] hover:bg-slate-50 transition-colors disabled:opacity-60">
                {savingTpl ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save as template
              </button>
            </div>
          </form>

          {/* Live preview */}
          <div className="lg:col-span-2">
            <div className="lg:sticky lg:top-24">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8] mb-2">Live preview</p>
              <div className="rounded-xl border border-[#E2E8F0] bg-white overflow-hidden shadow-sm">
                <div className="bg-[#0F172A] px-6 py-5 text-white">
                  <div className="flex items-center gap-2">
                    <ShieldCheck size={18} className="text-[#38BDF8]" />
                    <span className="font-bold">Credit Card Authorization</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">{f.merchant_name || "Merchant name"}</p>
                </div>
                <div className="p-6 space-y-4 text-sm">
                  <PreRow label="Customer" value={f.customer_name || "—"} />
                  <PreRow label="Merchant" value={f.merchant_name || "—"} />
                  <div className="rounded-lg bg-[#EFF6FF] px-4 py-3">
                    <p className="text-xs text-[#64748B]">Authorized amount</p>
                    <p className="text-2xl font-extrabold text-[#2563EB] font-mono">{money(f.amount)}</p>
                    <p className="text-xs text-[#64748B] capitalize mt-0.5">{f.frequency}{f.frequency_note ? ` · ${f.frequency_note}` : ""}</p>
                  </div>
                  {f.description && <PreRow label="For" value={f.description} />}
                  <div className="rounded-lg border border-dashed border-[#E2E8F0] px-4 py-3 text-[#94A3B8]">
                    <p className="text-xs">Card details &amp; signature</p>
                    <p className="text-xs mt-0.5">Collected securely from the customer</p>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-[#64748B] pt-1">
                    <Lock size={12} /> Only last 4 card digits stored · 256-bit SSL
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="rounded-xl border border-[#E2E8F0] bg-white p-5 space-y-4">
    <h3 className="text-sm font-bold text-[#0F172A]">{title}</h3>
    {children}
  </div>
);

const Field = ({ label, testid, ...rest }) => (
  <label className="block">
    <span className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">{label}</span>
    <input data-testid={testid} className="mt-1.5 w-full rounded-lg border border-[#E2E8F0] px-3 py-2.5 text-sm outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/10 transition-all" {...rest} />
  </label>
);

const PreRow = ({ label, value }) => (
  <div className="flex justify-between gap-4">
    <span className="text-[#64748B]">{label}</span>
    <span className="font-semibold text-[#0F172A] text-right">{value}</span>
  </div>
);
